import { Routes } from '@angular/router';

import { authGuard } from '@core/auth/auth.guard';
import { guestGuard } from '@core/auth/guest.guard';
import { Login } from '@features/login/login';

/**
 * The login island.
 *
 * Login is the ONLY eager component in the app, and that is deliberate. esbuild
 * splits on static reachability from main.ts, and Angular emits no modulepreload
 * hint for a lazy route chunk - so a lazy /login costs the browser a full serial
 * round trip of blank screen: fetch main, parse, discover the chunk, fetch it,
 * only then paint. Eager trades ~74kB of initial bundle for one round trip
 * instead of two, which is what "must render very fast" actually asks for. Most
 * of that 74kB is @angular/forms, which every feature form needs anyway, so in
 * main it is shared rather than duplicated per lazy chunk.
 *
 * The shell sits behind BOTH loadComponent and loadChildren, which is what keeps
 * the sidebar, topbar, MenuService and every feature out of the initial bundle.
 *
 * Every guard is canMatch, never canActivate: canActivate fetches the chunk and
 * then rejects, canMatch skips the route before resolution, so a non-permitted
 * lazy chunk is never downloaded at all.
 */
export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'login' },

  { path: 'login', component: Login, canMatch: [guestGuard] },

  {
    // Reached when a login returns mustChangePassword. The bootstrap token carries
    // ONLY Account.Get + Account.Upsert (AuthFlowService.BuildLoginResult), so no
    // business screen would load anyway - Get is in there purely so the header's
    // GetLoginUserDetailById and GetMyMenu do not 403 behind the form.
    //
    // That narrow claim set is also why the component refreshes the token before it
    // navigates to /app on success: MustChangePassword is false server-side by then,
    // but the token in memory is still the two-claim one, and PermissionService is a
    // computed over those claims - so landing on the shell without refreshing would
    // render every permission-gated control as denied.
    //
    // Same component as the in-app change-password dialog; it picks its mode from
    // whether a DialogRef is injectable. Full-page and non-dismissible here.
    path: 'change-password',
    loadComponent: () =>
      import('@features/account/change-password/change-password').then((m) => m.ChangePassword)
  },

  {
    path: 'app',
    loadComponent: () => import('@layout/shell/shell').then((m) => m.Shell),
    canMatch: [authGuard],
    data: { preload: true },
    // Every screen under /app, including the self-service account group, is mounted
    // by shell.routes.ts - NOT listed here. loadChildren rather than an inline
    // children array so the whole feature route table stays inside the lazy shell
    // chunk: app.routes.ts is eagerly reachable from main.ts, so a literal array
    // here would pull every loadComponent reference into the initial bundle's
    // module graph and undo the login island.
    loadChildren: () => import('./shell.routes').then((m) => m.shellRoutes)
  },

  {
    path: '**',
    loadComponent: () => import('@shared/not-found/not-found').then((m) => m.NotFound)
  }
];
